/**
 * Зведений реєстр договорів (Довідники) — ag-Grid Infinite + пошук + редактор.
 * Колонки: Контрагент / Назва / Номер / Дата / Строк дії / Валюта.
 * Редагування і створення прямо звідси (Drawer), не лише з картки контрагента.
 */
import { useMemo, useRef, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import {
  contractorNested, orgs, contractors as contractorsApi,
  type ContractRegistryItem, type VatMethod,
} from '@/api/catalogs';
import { refs } from '@/api/refs';
import { projects } from '@/api/projects';
import Drawer from '@/components/Drawer';
import DatePopover from '@/components/DatePopover';
import ImportedBadge from '@/shared/components/ImportedBadge';
import { useConfirm } from '@/components/ConfirmProvider';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useGridColumnWidths } from '@/hooks/useGridColumnWidths';

const VAT_METHOD_LABELS: Record<VatMethod, string> = {
  FIRST_EVENT: 'За першою подією',
  ON_PAYMENT: 'За касовим методом (за оплатою)',
};
const CONTRACT_TYPE_LABELS: Record<string, string> = {
  WITH_BUYER: 'З покупцем', WITH_SUPPLIER: 'З постачальником',
  WITH_COMMISSIONER: 'З комісіонером', WITH_COMMITTENT: 'З комітентом', OTHER: 'Iнше',
};
const CONTRACT_TYPES = Object.keys(CONTRACT_TYPE_LABELS);

interface FormState {
  id: string | null;          // null = створення
  organization_id: string;
  contractor_id: string;
  name: string;
  number: string;
  currency_id: string;
  contract_type: string;
  civil_kind_id: string;
  contract_date: string;
  valid_until: string;
  is_foreign_economic: boolean;
  vat_method: VatMethod;
  tare_vat_method: VatMethod;
  project_id: string;
}

const emptyForm = (): FormState => ({
  id: null, organization_id: '', contractor_id: '', name: '', number: '',
  currency_id: '', contract_type: 'OTHER', civil_kind_id: '',
  contract_date: '', valid_until: '', is_foreign_economic: false,
  vat_method: 'FIRST_EVENT', tare_vat_method: 'FIRST_EVENT', project_id: '',
});

export default function ContractsRegistryPage() {
  const confirm = useConfirm();
  const [search, setSearch] = useState('');
  const [orgFilter, setOrgFilter] = useState('');
  const [form, setForm] = useState<FormState | null>(null);

  const gridApiRef = useRef<GridApi<ContractRegistryItem> | null>(null);
  // Персист ширин колонок ag-Grid (дзеркало DataTable-персисту).
  const colW = useGridColumnWidths('contracts-registry');
  const { datasource, total } = useInfiniteGridDatasource<ContractRegistryItem>(
    (limit, offset, sort) => contractorNested.listRegistry({
      q: search || undefined, organization_id: orgFilter || undefined,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset,
    }),
    [search, orgFilter],
  );
  const refreshGrid = () => gridApiRef.current?.refreshInfiniteCache();

  // Довідники для форми.
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const currenciesQ = useQuery({ queryKey: ['currencies'], queryFn: refs.currencies });
  const civilKindsQ = useQuery({ queryKey: ['contract-civil-kinds'], queryFn: refs.contractCivilKinds.list });
  const projectsQ = useQuery({ queryKey: ['projects-for-contract'], queryFn: () => projects.list({ limit: 200 }), enabled: form !== null });
  const contractorsQ = useQuery({ queryKey: ['contractors-all'], queryFn: () => contractorsApi.list({ limit: 1000 }), enabled: form !== null });

  const uahId = (currenciesQ.data ?? []).find((c) => c.code === 'UAH')?.id ?? '';
  const currCode = (id: string) => (currenciesQ.data ?? []).find((c) => c.id === id)?.code ?? '';

  // Прапори ОБРАНОЇ у формі юрособи → умовні поля методів ПДВ.
  const selectedOrg = (orgsQ.data?.items ?? []).find((o) => o.id === form?.organization_id);
  const showVatMethod = selectedOrg?.is_vat_payer ?? false;
  const showTareVatMethod = selectedOrg?.use_returnable_packaging ?? false;

  const save = useMutation({
    mutationFn: () => {
      const f = form!;
      const common = {
        name: f.name.trim(), currency_id: f.currency_id || uahId,
        contract_type: f.contract_type, civil_kind_id: f.civil_kind_id || null,
        number: f.number.trim() || null, contract_date: f.contract_date || null,
        valid_until: f.valid_until || null, is_foreign_economic: f.is_foreign_economic,
        vat_method: f.vat_method, tare_vat_method: f.tare_vat_method,
        project_id: f.project_id || null,
      };
      if (f.id) return contractorNested.updateContract(f.id, common);
      return contractorNested.createContract({
        contractor_id: f.contractor_id, organization_id: f.organization_id, ...common,
      });
    },
    onSuccess: () => { refreshGrid(); setForm(null); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => contractorNested.removeContract(id),
    onSuccess: () => { refreshGrid(); setForm(null); },
  });

  const cols = useMemo<ColDef<ContractRegistryItem>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      // Значок «І» імпорту з 1С.
      cellRenderer: (p: { data?: ContractRegistryItem }) => <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'contractor_name', headerName: 'Контрагент', flex: 1, minWidth: 200, sortable: true },
    // Наша юрособа — backend віддає organization_name (LEFT JOIN), серверна
    // сортировка через o.name (whitelist). «—» якщо NULL (поза видимістю).
    { field: 'organization_name', headerName: 'Організація', flex: 1, minWidth: 160, sortable: true,
      valueFormatter: (p) => p.value ?? '—' },
    { field: 'name', headerName: 'Назва договору', flex: 1, minWidth: 180, sortable: true },
    { field: 'number', headerName: 'Номер', width: 120, sortable: true },
    { field: 'contract_date', headerName: 'Дата', width: 120, sortable: true,
      valueFormatter: (p) => p.value ?? '—' },
    { field: 'valid_until', headerName: 'Строк дії', width: 120, sortable: true,
      valueFormatter: (p) => p.value ?? 'безстроковий' },
    // Валюта — valueGetter без field, серверна сортировка по UUID немає сенсу.
    { headerName: 'Валюта', width: 90, sortable: false,
      valueGetter: (p) => (p.data ? currCode(p.data.currency_id) : '') },
  ], [currenciesQ.data]);

  function startEdit(c: ContractRegistryItem) {
    setForm({
      id: c.id, organization_id: c.organization_id, contractor_id: c.contractor_id,
      name: c.name, number: c.number ?? '', currency_id: c.currency_id,
      contract_type: c.contract_type, civil_kind_id: c.civil_kind_id ?? '',
      contract_date: c.contract_date ?? '', valid_until: c.valid_until ?? '',
      is_foreign_economic: c.is_foreign_economic, vat_method: c.vat_method,
      tare_vat_method: c.tare_vat_method, project_id: c.project_id ?? '',
    });
  }

  const f = form;
  const canSave = !!f && !!f.name.trim() && !!f.organization_id && !!f.contractor_id && !save.isPending;

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          Реєстр договорів
          {total !== null && (
            <span style={{ fontSize: 13, color: 'var(--muted)', marginLeft: 8, fontWeight: 400 }}>({total})</span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input type="search" placeholder="Пошук № / назва / контрагент"
            value={search} onChange={(e) => setSearch(e.target.value)} />
          <select value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)}>
            <option value="">Усі юрособи</option>
            {(orgsQ.data?.items ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <button className="primary" onClick={() => setForm(emptyForm())}>+ Новий договір</button>
        </div>
      </div>

      <div className="ag-theme-quartz grid">
        <AgGridReact<ContractRegistryItem>
          rowModelType="infinite"
          datasource={datasource}
          cacheBlockSize={100}
          columnDefs={cols}
          onGridReady={(e: GridReadyEvent<ContractRegistryItem>) => { gridApiRef.current = e.api; colW.applyTo(e.api); }}
          onColumnResized={colW.onColumnResized}
          onRowDoubleClicked={(e) => e.data && startEdit(e.data)}
          rowSelection="single"
        />
      </div>

      <Drawer
        open={form !== null}
        width="wide"
        title={f?.id ? `Договір: ${f.name}` : 'Новий договір'}
        onClose={() => setForm(null)}
        footer={
          <>
            {f?.id && (
              <button className="danger"
                onClick={() => confirm(`Видалити договір «${f.name}»?`).then((ok) => { if (ok) remove.mutate(f.id!); })}>
                Видалити
              </button>
            )}
            <button onClick={() => setForm(null)}>Скасувати</button>
            <button className="primary" disabled={!canSave} onClick={() => save.mutate()}>Зберегти</button>
          </>
        }
      >
        {f && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', padding: '0.5rem' }}>
            <label>Контрагент <span style={{ color: 'red' }}>*</span>
              <select value={f.contractor_id} disabled={!!f.id}
                onChange={(e) => setForm({ ...f, contractor_id: e.target.value })} style={{ width: '100%' }}>
                <option value="">— оберіть —</option>
                {(contractorsQ.data?.items ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label>Наша юрособа <span style={{ color: 'red' }}>*</span>
              <select value={f.organization_id} disabled={!!f.id}
                onChange={(e) => setForm({ ...f, organization_id: e.target.value })} style={{ width: '100%' }}>
                <option value="">— оберіть —</option>
                {(orgsQ.data?.items ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>
            <label>Назва <span style={{ color: 'red' }}>*</span>
              <input value={f.name} onChange={(e) => setForm({ ...f, name: e.target.value })} style={{ width: '100%' }} />
            </label>
            <label>Номер
              <input value={f.number} onChange={(e) => setForm({ ...f, number: e.target.value })} style={{ width: '100%' }} />
            </label>
            <label>Валюта
              <select value={f.currency_id || uahId} onChange={(e) => setForm({ ...f, currency_id: e.target.value })} style={{ width: '100%' }}>
                {(currenciesQ.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
              </select>
            </label>
            <label>Тип договору
              <select value={f.contract_type} onChange={(e) => setForm({ ...f, contract_type: e.target.value })} style={{ width: '100%' }}>
                {CONTRACT_TYPES.map((t) => <option key={t} value={t}>{CONTRACT_TYPE_LABELS[t]}</option>)}
              </select>
            </label>
            <label>Вид за ЦК
              <select value={f.civil_kind_id} onChange={(e) => setForm({ ...f, civil_kind_id: e.target.value })} style={{ width: '100%' }}>
                <option value="">— не вказано —</option>
                {(civilKindsQ.data ?? []).map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
              </select>
            </label>
            {showVatMethod && (
              <label>Метод ПДВ
                <select value={f.vat_method} onChange={(e) => setForm({ ...f, vat_method: e.target.value as VatMethod })} style={{ width: '100%' }}>
                  <option value="FIRST_EVENT">{VAT_METHOD_LABELS.FIRST_EVENT}</option>
                  <option value="ON_PAYMENT">{VAT_METHOD_LABELS.ON_PAYMENT}</option>
                </select>
              </label>
            )}
            {showTareVatMethod && (
              <label>Метод ПДВ по тарі
                <select value={f.tare_vat_method} onChange={(e) => setForm({ ...f, tare_vat_method: e.target.value as VatMethod })} style={{ width: '100%' }}>
                  <option value="FIRST_EVENT">{VAT_METHOD_LABELS.FIRST_EVENT}</option>
                  <option value="ON_PAYMENT">{VAT_METHOD_LABELS.ON_PAYMENT}</option>
                </select>
              </label>
            )}
            <label>Проект (за замовч.)
              <select value={f.project_id} onChange={(e) => setForm({ ...f, project_id: e.target.value })} style={{ width: '100%' }}>
                <option value="">— не вказано —</option>
                {(projectsQ.data?.items ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <label>Дата
              <DatePopover value={f.contract_date} onChange={(v) => setForm({ ...f, contract_date: v })} style={{ width: '100%' }} />
            </label>
            <label>Строк дії
              <DatePopover value={f.valid_until} onChange={(v) => setForm({ ...f, valid_until: v })} style={{ width: '100%' }} />
            </label>
            <label style={{ gridColumn: '1 / -1' }}>
              <input type="checkbox" checked={f.is_foreign_economic}
                onChange={(e) => setForm({ ...f, is_foreign_economic: e.target.checked })}
                style={{ width: 'auto', marginRight: '0.4rem' }} />
              ЗЕД-договір
            </label>
          </div>
        )}
      </Drawer>
    </>
  );
}
